import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import bizApi, { apiError } from "@/lib/businessApi";
import { inputCls } from "@/components/booking/BookingFields";
import { useLang } from "@/lib/i18n";
import { useBiz } from "@/lib/businessI18n";

const TOGGLE_KEYS = ["monthly_billing", "whatsapp", "flight_tracking", "require_cost_center"];
const BUSINESS_TYPES = ["company", "hotel", "hotel_group", "concierge", "travel_agency", "event_agency", "clinic", "family_office", "other"];
const CYCLES = ["monthly", "weekly", "biweekly", "per_mission"];

export default function Settings({ org, onSaved }) {
  const { t } = useLang();
  const { t: tb } = useBiz();
  const [orgName, setOrgName] = useState(org.name);
  const [billingEmail, setBillingEmail] = useState(org.billing_email || "");
  const [costCenters, setCostCenters] = useState(org.cost_centers || []);
  const [newCenter, setNewCenter] = useState("");
  const [toggles, setToggles] = useState(org.toggles || {});
  const [businessType, setBusinessType] = useState(org.business_type || "company");
  const [cycle, setCycle] = useState(org.billing?.cycle || "monthly");
  const [locations, setLocations] = useState(org.locations || []);
  const [newLocation, setNewLocation] = useState({ name: "", address: "" });
  const [activity, setActivity] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    bizApi.get("/activity").then((r) => setActivity(r.data)).catch(() => {});
  }, []);

  const addCenter = () => {
    const value = newCenter.trim();
    if (value && !costCenters.includes(value)) setCostCenters((list) => [...list, value]);
    setNewCenter("");
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await bizApi.patch("/settings", {
        org_name: orgName, billing_email: billingEmail, cost_centers: costCenters, toggles,
      });
      const profile = await bizApi.patch("/settings/profile", {
        business_type: businessType, billing: { cycle },
      });
      onSaved(profile.data || data);
      toast.success(t("bz.st.saved_t"));
    } catch (err) {
      toast.error(apiError(err, t("bz.st.err")));
    } finally {
      setBusy(false);
    }
  };

  const addLocation = async () => {
    if (!newLocation.name.trim()) return;
    try {
      const r = await bizApi.post("/locations", newLocation);
      setLocations((list) => [...(list || []), r.data]);
      setNewLocation({ name: "", address: "" });
    } catch (err) {
      toast.error(apiError(err, t("bz.st.err")));
    }
  };

  const removeLocation = async (id) => {
    await bizApi.delete(`/locations/${id}`).catch(() => {});
    setLocations((list) => (list || []).filter((l) => l.id !== id));
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl space-y-5" data-testid="settings-panel">
      <div className="rounded-3xl border border-ink/5 bg-white p-6 shadow-sm">
        <p className="text-sm font-bold text-ink">{t("bz.st.org_t")}</p>
        <div className="mt-5 space-y-4">
          <div>
            <p className="mb-2 text-sm font-medium text-slate">{t("bz.st.org_name")}</p>
            <input value={orgName} onChange={(e) => setOrgName(e.target.value)} data-testid="settings-org-name" className={inputCls} />
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-slate">{t("bz.st.billing")}</p>
            <input type="email" value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} data-testid="settings-billing-email" className={inputCls} />
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-ink/5 bg-white p-6 shadow-sm">
        <p className="text-sm font-bold text-ink">{t("bz.st.cc_t")}</p>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="settings-cost-centers">
          {costCenters.map((center) => (
            <span key={center} className="inline-flex items-center gap-2 rounded-full bg-miles-ice px-4 py-2 text-xs font-semibold text-miles">
              {center}
              <button type="button" onClick={() => setCostCenters((list) => list.filter((c) => c !== center))}
                data-testid={`settings-cc-remove-${center}`} className="text-miles/60 transition-colors hover:text-miles">
                <X className="h-3 w-3" strokeWidth={2} />
              </button>
            </span>
          ))}
          {costCenters.length === 0 && <p className="text-xs text-slate">{t("bz.st.cc_empty")}</p>}
        </div>
        <div className="mt-4 flex gap-2">
          <input value={newCenter} onChange={(e) => setNewCenter(e.target.value)} placeholder={t("bz.st.cc_ph")}
            onKeyDown={(e) => e.key === "Enter" && addCenter()} data-testid="settings-cc-input" className={`${inputCls} h-11 text-sm`} />
          <button type="button" onClick={addCenter} data-testid="settings-cc-add"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-miles text-white transition-colors hover:bg-miles-dark">
            <Plus className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-ink/5 bg-white p-6 shadow-sm">
        <p className="text-sm font-bold text-ink">{t("bz.st.toggles_t")}</p>
        <div className="mt-3 space-y-1">
          {TOGGLE_KEYS.map((key) => {
            const [title, desc] = t(`bz.st.toggles.${key}`);
            const active = Boolean(toggles[key]);
            return (
              <button key={key} type="button" onClick={() => setToggles((tg) => ({ ...tg, [key]: !tg[key] }))}
                data-testid={`settings-toggle-${key}`} aria-pressed={active}
                className="flex w-full items-center justify-between gap-4 rounded-2xl px-3 py-4 text-left transition-colors hover:bg-cloud">
                <div>
                  <p className="text-sm font-semibold text-ink">{title}</p>
                  <p className="text-xs text-slate">{desc}</p>
                </div>
                <span className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-300 ${active ? "bg-miles" : "bg-ink/15"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all duration-300 ${active ? "left-[22px]" : "left-0.5"}`} />
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-3xl border border-ink/5 bg-white p-6 shadow-sm" data-testid="settings-profile">
        <p className="text-sm font-bold text-ink">{tb("settings.profile")}</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div>
            <p className="mb-2 text-sm font-medium text-slate">{tb("settings.business_type")}</p>
            <select value={businessType} onChange={(e) => setBusinessType(e.target.value)}
              data-testid="settings-business-type" className={inputCls}>
              {BUSINESS_TYPES.map((bt) => <option key={bt} value={bt}>{tb(`settings.bt.${bt}`)}</option>)}
            </select>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-slate">{tb("billing.cycle")}</p>
            <select value={cycle} onChange={(e) => setCycle(e.target.value)}
              data-testid="settings-billing-cycle" className={inputCls}>
              {CYCLES.map((c) => <option key={c} value={c}>{tb(`billing.cycle.${c}`)}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-ink/5 bg-white p-6 shadow-sm" data-testid="settings-locations">
        <p className="text-sm font-bold text-ink">{tb("settings.locations")}</p>
        <div className="mt-3 space-y-2">
          {(locations || []).map((loc) => (
            <div key={loc.id} className="flex items-center gap-3 rounded-2xl border border-ink/5 px-4 py-3" data-testid={`location-${loc.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-ink">{loc.name}</p>
                <p className="truncate text-xs text-slate">{loc.address}</p>
              </div>
              <button type="button" onClick={() => removeLocation(loc.id)} data-testid={`location-delete-${loc.id}`}
                className="text-slate transition-colors hover:text-red-500">
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <input value={newLocation.name} onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
            placeholder={tb("groups.name")} data-testid="location-name" className={`${inputCls} h-11 text-sm`} />
          <input value={newLocation.address} onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })}
            placeholder={tb("places.address")} data-testid="location-address" className={`${inputCls} h-11 text-sm`} />
          <button type="button" onClick={addLocation} data-testid="location-add"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-miles text-white transition-colors hover:bg-miles-dark">
            <Plus className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-ink/5 bg-white p-6 shadow-sm" data-testid="settings-activity">
        <p className="text-sm font-bold text-ink">{tb("settings.activity")}</p>
        <div className="mt-3 max-h-64 space-y-0 overflow-y-auto">
          {activity.length === 0 && <p className="text-xs text-slate">—</p>}
          {activity.slice(0, 40).map((e) => (
            <div key={e.id} className="flex items-baseline gap-3 border-b border-ink/[0.04] py-2 text-xs" data-testid={`activity-${e.id}`}>
              <span className="shrink-0 font-mono text-slate">{(e.at || "").slice(0, 16).replace("T", " ")}</span>
              <span className="text-ink">{e.message}</span>
            </div>
          ))}
        </div>
      </div>

      <button type="button" onClick={save} disabled={busy} data-testid="settings-save"
        className="inline-flex items-center gap-2 rounded-full bg-miles px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_28px_rgba(36,87,255,0.3)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-miles-dark disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : <Save className="h-4 w-4" strokeWidth={1.5} />}
        {t("bz.st.save")}
      </button>
    </motion.div>
  );
}
